import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DeepLinkPage } from './DeepLinkPage';

/**
 * Redirect page for Stripe Connect onboarding returns from mobile
 * This page handles the return_url from Stripe and redirects to the mobile app
 * 
 * Supports returnPath parameter for redirecting to specific screens after Stripe flow
 */
export default function BillingSettingsRedirect() {
  const [searchParams] = useSearchParams();
  const returnParam = searchParams.get('return');
  const refreshParam = searchParams.get('refresh');
  const platform = searchParams.get('platform');
  const returnPath = searchParams.get('returnPath');

  // Determine the deep link based on environment
  const isLocalhost = typeof window !== 'undefined' && 
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const deepLinkScheme = isLocalhost ? 'rollops-dev://' : 'rollops://';
  
  // Determine the target path - use returnPath if provided, otherwise default to billing-settings
  // returnPath should be URL-decoded and used as the path after the scheme
  const targetPath = returnPath 
    ? decodeURIComponent(returnPath).replace(/^\//, '') // Remove leading slash if present
    : 'billing-settings';
  
  // Build the deep link with query parameters
  const queryParams = new URLSearchParams();
  if (returnParam === 'true') {
    queryParams.set('return', 'true');
  }
  if (refreshParam === 'true') {
    queryParams.set('refresh', 'true');
  }
  const queryString = queryParams.toString();
  const deepLink = `${deepLinkScheme}${targetPath}${queryString ? `?${queryString}` : ''}`;

  // Auto-detect if this is a mobile redirect scenario
  // Only show deep link page if explicitly platform=mobile OR if returnPath is provided (indicating mobile return)
  // On localhost without platform=mobile, redirect to web billing settings
  const shouldShowDeepLink = platform === 'mobile' || (isLocalhost && returnPath !== null);

  // If not a mobile redirect scenario, redirect to web billing settings
  useEffect(() => {
    if (!shouldShowDeepLink) {
      const params = new URLSearchParams();
      if (returnParam === 'true') params.set('return', 'true');
      if (refreshParam === 'true') params.set('refresh', 'true');
      window.location.replace(`/billing/settings?${params.toString()}`);
    }
  }, [returnParam, refreshParam, shouldShowDeepLink]);

  // Show deep link page for mobile or localhost (simulator)
  if (shouldShowDeepLink) {
    return (
      <DeepLinkPage
        title="Returning to RollOps"
        description={isLocalhost 
          ? "Opening the RollOps app in your simulator..." 
          : "Redirecting you back to the RollOps app..."}
        deepLink={deepLink}
      />
    );
  }

  return null;
}

